import React, { useState } from "react";
import type { ColumnsType } from "antd/es/table";
import Table from "antd/es/table";
import { FiChevronDown } from "react-icons/fi";
import { Colors } from "../../utils/utils.colors";

const ChevronI = FiChevronDown as any;

const columns: ColumnsType<ItemFrais> = [
  { title: "Promotion", dataIndex: "promotion", key: "promotion", fixed: "left", width: 100, align: "center" },
  { title: "Faculté", dataIndex: "faculte", key: "faculte", fixed: "left", width: 170, align: "left" },
  {
    title: "TOTAL",
    dataIndex: "total",
    key: "total",
    align: "center",
    className: "bg-primary",
    width: 100,
    render: (total: number) => <strong style={{ color: Colors.whiteColor, fontWeight: 700 }}>{total}$</strong>,
  },
  { title: "Frais Acad.", dataIndex: ["details_frais", "frais_academiques"], key: "frais_academiques", align: "center", width: 90 },
  { title: "Relevé", dataIndex: ["details_frais", "releve"], key: "releve", align: "center", width: 70, render: (val: number) => (val === 0 ? "-" : `${val}$`) },
  { title: "Réinscrip.", dataIndex: ["details_frais", "reinscription"], key: "reinscription", align: "center", width: 80, render: (val: number | null) => (val === 0 || val === null ? "-" : `${val}$`) },
  { title: "Carte", dataIndex: ["details_frais", "carte"], key: "carte", align: "center", width: 60, render: (val: number) => (val === 0 ? "-" : `${val}$`) },
  { title: "Biblio.", dataIndex: ["details_frais", "bibliotheque"], key: "bibliotheque", align: "center", width: 70, render: (val: number) => (val === 0 ? "-" : `${val}$`) },
  { title: "Coord.", dataIndex: ["details_frais", "coordination"], key: "coordination", align: "center", width: 70, render: (val: number) => (val === 0 ? "-" : `${val}$`) },
  { title: "Diplôme", dataIndex: ["details_frais", "diplome"], key: "diplome", align: "center", width: 70, render: (val: number) => (val === 0 ? "-" : `${val}$`) },
  { title: "Construc.", dataIndex: ["details_frais", "construction"], key: "construction", align: "center", width: 80, render: (val: number) => (val === 0 ? "-" : `${val}$`) },
  { title: "Stage", dataIndex: ["details_frais", "stage"], key: "stage", align: "center", width: 70, render: (val: number) => (val === 0 ? "-" : `${val}$`) },
  { title: "Jury", dataIndex: ["details_frais", "jury"], key: "jury", align: "center", width: 70, render: (val: number) => (val === 0 ? "-" : `${val}$`) },
  { title: "1ère Tr.", dataIndex: ["tranches", "premiere"], key: "premiere", align: "center", width: 80 },
  { title: "2ème Tr.", dataIndex: ["tranches", "deuxieme"], key: "deuxieme", align: "center", width: 80 },
  { title: "3ème Tr.", dataIndex: ["tranches", "troisieme"], key: "troisieme", align: "center", width: 80 },
];

const DETAIL_LABELS: { key: keyof DetailsFrais; label: string }[] = [
  { key: "frais_academiques", label: "Frais académiques" },
  { key: "releve", label: "Relevé" },
  { key: "reinscription", label: "Réinscription" },
  { key: "carte", label: "Carte" },
  { key: "bibliotheque", label: "Bibliothèque" },
  { key: "coordination", label: "Coordination" },
  { key: "diplome", label: "Diplôme" },
  { key: "construction", label: "Construction" },
  { key: "stage", label: "Stage" },
  { key: "jury", label: "Jury" },
];

const FeeCard: React.FC<{ item: ItemFrais }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  const details = DETAIL_LABELS.filter(({ key }) => {
    const val = item.details_frais[key];
    return val !== null && val !== 0;
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        style={{ background: Colors.primaryColor }}
      >
        <div className="min-w-0">
          <span className="inline-block rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-bold text-white tracking-wide">
            {item.promotion}
          </span>
          <div className="mt-1 text-xs text-white/85 truncate">{item.faculte}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <div className="text-xl font-extrabold text-white leading-none">{item.total}$</div>
            <div className="text-[10px] text-white/70 mt-0.5">total</div>
          </div>
          <ChevronI
            size={18}
            className="text-white/80 transition-transform"
            style={{ transform: open ? "rotate(180deg)" : "none" }}
          />
        </div>
      </button>

      {open && (
        <div className="px-4 py-3">
          {details.length > 0 && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-3">
              {details.map(({ key, label }) => (
                <div key={key} className="flex justify-between gap-2 border-b border-slate-100 pb-1.5">
                  <span className="text-slate-500">{label}</span>
                  <span className="font-semibold text-slate-800">{item.details_frais[key]}$</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <span>1ère tr. <b className="text-slate-800">{item.tranches.premiere}$</b></span>
            <span>2ème tr. <b className="text-slate-800">{item.tranches.deuxieme}$</b></span>
            <span>3ème tr. <b className="text-slate-800">{item.tranches.troisieme}$</b></span>
          </div>
        </div>
      )}
    </div>
  );
};

export const FeesGrid: React.FC<{ data: ItemFrais[] }> = ({ data }) => (
  <>
    <div className="hidden md:block" style={{ width: "100%" }}>
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        bordered
        size="small"
        scroll={{ x: 1200 }}
        components={{
          header: {
            cell: (props: any) => (
              <th
                {...props}
                style={{ ...props.style, backgroundColor: Colors.primaryColor, color: Colors.whiteColor }}
              />
            ),
          },
        }}
      />
    </div>

    <div className="md:hidden flex flex-col gap-3">
      {data.map((item) => (
        <FeeCard key={item.key} item={item} />
      ))}
    </div>
  </>
);
